import SensorsWave from '@sensorswave/js-sdk'
import {
  TrackingEvents,
  TrackingSource,
  type AppTracking,
  type TabClickTrackingOptions,
  type TrackingAppName,
  type TrackingClientOptions,
  type TrackingIdentifyExtras,
  type TrackingSourceValue,
  type TrackingUserLike
} from './types'

export * from './types'

/** 登出时需清除的自定义全局属性（不含前缀，清除时会拼上 ccw_ / vas_） */
const LOGIN_SCOPED_KEYS = [
  'user_id',
  'tenant_id',
  'tenant_name',
  'branch_id',
  'branch_name',
  'user_role',
  'user_name',
  'source'
] as const

const SOURCE_QUERY_KEY = 'source'
const SOURCE_STORAGE_KEY = 'cw_tracking_source'
const VALID_SOURCES = new Set<string>(Object.values(TrackingSource))

/** control-tower → ccw_，vas → vas_（仅自定义属性；SDK 自动采集字段不加前缀） */
function getCustomPropPrefix(appName: TrackingAppName): 'ccw_' | 'vas_' {
  return appName === 'vas' ? 'vas_' : 'ccw_'
}

/** 自定义属性 key，如 customPropKey('ccw_', 'tenant_id') → ccw_tenant_id */
function customPropKey(prefix: 'ccw_' | 'vas_', key: string): string {
  return `${prefix}${key}`
}

function resolveTrackingSource(raw: string | null | undefined): TrackingSourceValue | null {
  if (!raw) return null
  const value = raw.trim().toLowerCase()
  return VALID_SOURCES.has(value) ? (value as TrackingSourceValue) : null
}

export function createAppTracking(options: TrackingClientOptions): AppTracking {
  const {
    enabled = true,
    token,
    apiHost,
    appName,
    endpoint,
    appVersion = '',
    env = '',
    platform = 'web',
    debug = false,
    autoCapture = true,
    isSinglePageApp = true,
    enableClickTrack = true
  } = options

  const prefix = getCustomPropPrefix(appName)
  let inited = false
  let tabTrackingBound = false
  /** init 之外的全局属性（登录态、来源等）；SDK registerCommonProperties 会整表替换，不能与 base 分开注册 */
  let extraCommonProps: Record<string, string> = {}
  const isReady = () => enabled && inited

  const getBaseCommonProps = (): Record<string, string> => ({
    app_name: appName,
    [customPropKey(prefix, 'endpoint')]: endpoint,
    [customPropKey(prefix, 'platform')]: platform,
    [customPropKey(prefix, 'app_version')]: appVersion,
    [customPropKey(prefix, 'env')]: env
  })

  const applyCommonProperties = () => {
    if (!isReady()) return
    SensorsWave.registerCommonProperties({
      ...getBaseCommonProps(),
      ...extraCommonProps
    })
  }

  const initTracking = () => {
    if (!enabled || inited) return
    if (!token || !apiHost) {
      console.warn('[CT_TRACKING] 缺少 token 或 apiHost，跳过神策初始化')
      return
    }

    SensorsWave.init(token, { apiHost, autoCapture, isSinglePageApp, enableClickTrack, debug })
    applyCommonProperties()
    inited = true
  }

  /** 自定义事件属性统一加 ccw_ / vas_ 前缀后上报 */
  const trackEvent = (eventName: string, properties: Record<string, unknown> = {}) => {
    if (!isReady()) return

    const prefixedProps: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(properties)) {
      if (value === undefined || value === null || value === '') continue
      prefixedProps[customPropKey(prefix, key)] = value
    }
    SensorsWave.trackEvent(eventName, prefixedProps)
  }

  const syncTrackingIdentify = (
    user: TrackingUserLike | null | undefined,
    extras?: TrackingIdentifyExtras
  ) => {
    if (!isReady() || !user?.id) return

    const loginId = String(user.id)
    const tenantId = extras?.tenantId || user.tenantId
    const tenantName = extras?.tenantName || user.tenantName
    const branchId = extras?.branchId || user.branchId
    const branchName = extras?.branchName || user.branchName
    const userRole = user.roleType !== undefined && user.roleType !== null ? String(user.roleType) : undefined
    const userName = user.username || user.realName

    extraCommonProps[customPropKey(prefix, 'user_id')] = loginId
    if (userName) extraCommonProps[customPropKey(prefix, 'user_name')] = userName
    else delete extraCommonProps[customPropKey(prefix, 'user_name')]
    if (tenantId) extraCommonProps[customPropKey(prefix, 'tenant_id')] = tenantId
    else delete extraCommonProps[customPropKey(prefix, 'tenant_id')]
    if (tenantName) extraCommonProps[customPropKey(prefix, 'tenant_name')] = tenantName
    else delete extraCommonProps[customPropKey(prefix, 'tenant_name')]
    if (branchId) extraCommonProps[customPropKey(prefix, 'branch_id')] = branchId
    else delete extraCommonProps[customPropKey(prefix, 'branch_id')]
    if (branchName) extraCommonProps[customPropKey(prefix, 'branch_name')] = branchName
    else delete extraCommonProps[customPropKey(prefix, 'branch_name')]
    if (userRole) extraCommonProps[customPropKey(prefix, 'user_role')] = userRole
    else delete extraCommonProps[customPropKey(prefix, 'user_role')]

    // 必须先注册全局属性，再 identify；否则 SDK 自动发出的 $Identify 拿不到 ccw_* / app_name
    applyCommonProperties()

    if (SensorsWave.getLoginId() !== loginId) {
      SensorsWave.identify(loginId)
    }
  }

  const syncTrackingSource = (): TrackingSourceValue | null => {
    if (!isReady() || typeof window === 'undefined') return null

    const fromUrl = resolveTrackingSource(new URLSearchParams(window.location.search).get(SOURCE_QUERY_KEY))
    const source = fromUrl ?? resolveTrackingSource(sessionStorage.getItem(SOURCE_STORAGE_KEY))
    if (!source) return null

    if (fromUrl) sessionStorage.setItem(SOURCE_STORAGE_KEY, source)
    extraCommonProps[customPropKey(prefix, 'source')] = source
    applyCommonProperties()
    return source
  }

  const resetTracking = () => {
    if (!isReady()) return
    trackEvent(TrackingEvents.UserLogout, {})
    for (const key of LOGIN_SCOPED_KEYS) {
      delete extraCommonProps[customPropKey(prefix, key)]
    }
    applyCommonProperties()
  }

  // SDK WebClick 不采集 div.arco-tabs-tab；路由跳转型 tab 加 data-tab-track-ignore 排除
  const setupTabClickTracking = (opts: TabClickTrackingOptions = {}) => {
    if (typeof document === 'undefined' || tabTrackingBound) return () => {}

    const tabSelector = opts.tabSelector || '.arco-tabs-tab'
    const titleSelector = opts.titleSelector || '.arco-tabs-tab-title'
    const ignoreAttribute = opts.ignoreAttribute || 'data-tab-track-ignore'

    const handler = (event: Event) => {
      const target = event.target as HTMLElement | null
      if (!target?.closest) return

      const tab = target.closest<HTMLElement>(tabSelector)
      if (!tab || tab.closest(`[${ignoreAttribute}]`)) return

      const titleEl = tab.querySelector<HTMLElement>(titleSelector)
      const tabName = ((titleEl?.textContent ?? tab.textContent) || '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 100)
      if (!tabName) return

      trackEvent(TrackingEvents.TabSwitch, { tab_name: tabName })
    }

    document.addEventListener('click', handler, true)
    tabTrackingBound = true
    return () => {
      document.removeEventListener('click', handler, true)
      tabTrackingBound = false
    }
  }

  return {
    initTracking,
    syncTrackingIdentify,
    syncTrackingSource,
    resetTracking,
    trackEvent,
    setupTabClickTracking
  }
}
